import React, { useEffect, useMemo, useState } from "react";
import {
  AlertComponent,
  Button,
  MUITable,
  PaginationComponent,
  SearchInputComponent,
} from "../../../../../common/components";
import { Badge } from "@mui/material";
import _, { capitalize } from "lodash";
import { useResourcePermissions } from "../../../../../common/hooks/resource-permissions";
import MODULES from "../../../../../constants/modules";
import "./transactions-list.scss";
import { getTransactionListHeaders } from "../../order-management-helper";
import { SortOption } from "../../../../tenant/tenant-list/tenant-list-helper";
import { useSetLoading } from "../../../../../common/hooks/appearance";
import AddAdjustmentPopup from "./add-adjustment-popup";
import { getTransactionsById } from "../../../../../api/order-management";
import { formatDateTime } from "../../../../../utils/utils";
import icons from "../../../../../assets/icons/icons";
import TransactionFilters from "./transaction-filters/transaction-filters";

interface TransactionsListProps {
  invoiceId: number;
}

const TransactionsList: React.FC<TransactionsListProps> = ({ invoiceId }) => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize] = useState<number>(15);
  const [searchText, setSearchText] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [sortOption, setSortOption] = useState<SortOption>({
    sortBy: null,
    sortDirection: null,
  });
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false);
  const [filters, setFilters] = useState<any>({});
  const [alert, setAlert] = useState<{
    isOpen?: boolean;
    message?: any;
    type?: string;
  }>({});
  const [totalRecords, setTotalRecords] = useState<number>(0);

  const { canWrite } = useResourcePermissions(MODULES.ORDER_MANAGEMENT);
  const headers = getTransactionListHeaders();

  const handleSearch = _.debounce((text: string) => {
    setSearchText(text);
    setCurrentPage(1);
  }, 500);

  const fetchTransactions = async () => {
    setIsLoading(true);
    try {
      const params: any = {
        offset: currentPage - 1,
        limit: pageSize,
        searchText,
        sortBy: sortOption.sortBy,
        sortDirection: sortOption.sortDirection,
        ...filters,
      };
      const response = await getTransactionsById(invoiceId, params);
      setTransactions(response.data?.data?.transactions || []);
      setTotalRecords(response.data?.data?.totalRecords);
    } catch (error) {
      console.error("Error fetching transactions:", error);
      setAlert({
        isOpen: true,
        message: "Failed to fetch transactions. Please try again.",
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, [invoiceId, currentPage, pageSize, searchText, sortOption, filters]);

  const mapColumnNameToField = (columnName: string): string => {
    const mapping: Record<string, string> = {
      transactionId: "transaction_number",
      customerName: "first_name",
      dataTime: "transaction_date",
      totalAmount: "amount",
      status: "status",
      paymentMethod: "payment_method",
    };

    return mapping[columnName] || columnName;
  };

  const handleAddClick = () => {
    setIsDialogOpen(true);
  };

  const handleSort = (column: string, sortType: string) => {
    let sortValues: SortOption = { sortBy: null, sortDirection: null };
    if (sortType !== "Unsort") {
      const mappedField = mapColumnNameToField(column);
      sortValues = { sortBy: mappedField, sortDirection: sortType };
    }
    setCurrentPage(1);
    setSortOption(sortValues);
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setCurrentPage(value);
  };

  const formattedData = useMemo(() => {
    return transactions?.map((item: any) => {
      const {
        id,
        transaction_number,
        status,
        payment_method,
        amount,
        transaction_date,
        customer_name,
      } = item;

      const totalAmount = amount
        ? new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
            minimumFractionDigits: 0,
          }).format(amount)
        : "-";

      return {
        id: id,
        transactionId: transaction_number,
        status: capitalize(status) || "-",
        paymentMethod: payment_method ?? "-",
        dataTime: formatDateTime(transaction_date) || "-",
        totalAmount: totalAmount || "-",
        customerName: customer_name || "-",
      };
    });
  }, [transactions]);

  const showLoading = useMemo(() => isLoading, [isLoading]);
  useSetLoading(showLoading);

  const noResultsTxt = useMemo(() => {
    if (isLoading) {
      return "Loading Transactions...";
    }
    if (searchText) {
      return "No transactions found.";
    }
    return "No transactions available.";
  }, [isLoading, searchText]);

  const appliedFilterCount = useMemo(() => {
    let count = 0;
    const dateRangeFilterActive =
      filters.transactionFromDate || filters.transactionToDate;
    const amountFilterActive = filters.minAmount || filters.maxAmount;

    const otherFilters = { ...filters };
    delete otherFilters.transactionFromDate;
    delete otherFilters.transactionToDate;
    delete otherFilters.minAmount;
    delete otherFilters.maxAmount;

    const otherActiveFilters = Object.values(otherFilters).filter(
      (val) =>
        val !== undefined &&
        val !== "" &&
        val !== null &&
        !(Array.isArray(val) && val.length === 0)
    ).length;

    if (dateRangeFilterActive) {
      count += 1;
    }
    if (amountFilterActive) {
      count += 1;
    }

    count += otherActiveFilters;

    return count;
  }, [filters]);

  return (
    <div className="transactions-list-wrapper">
      <div className="transactions-list-filter-wrapper">
        <div className="search-input-wrapper">
          <SearchInputComponent
            placeholder="Search by customer, transaction ID etc.,"
            onSearch={handleSearch}
          />
        </div>
        <div className="filter-right-section d-flex">
          <Badge
            badgeContent={appliedFilterCount}
            invisible={appliedFilterCount === 0}
            color="secondary"
            overlap="circular"
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            sx={{
              "& .MuiBadge-badge": {
                top: "1px",
                right: "14px",
                transform: "scale(1)",
              },
            }}
          >
            <div
              className={`filter-icon ${isFilterOpen ? "active-filter" : ""}`}
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              style={{ cursor: "pointer" }}
            >
              <img src={icons.FilterIcon} alt="filter-icon" />
            </div>
          </Badge>
          {canWrite ? (
            <Button label="Add Adjustment" onClick={handleAddClick} />
          ) : (
            <></>
          )}
        </div>
      </div>
      <div className="transactions-list-table-filter">
        <div className="transactions-list-table">
          <MUITable
            headers={headers}
            data={formattedData}
            onSort={handleSort}
            noResultsTxt={noResultsTxt}
          />
        </div>
        {isFilterOpen ? (
          <TransactionFilters
            open={isFilterOpen}
            filters={filters}
            onClose={() => setIsFilterOpen(false)}
            onApplyFilters={(filterData, shouldClose = true) => {
              try {
                setFilters(filterData);
                if (shouldClose) setIsFilterOpen(false);
                setCurrentPage(1);
              } catch (error: any) {
                console.error("Failed to apply filters:", error);
                setAlert({
                  isOpen: true,
                  message: "Failed to apply filters. Please try again.",
                  type: "error",
                });
              }
            }}
          />
        ) : (
          <></>
        )}
      </div>

      {transactions?.length ? (
        <div className="orders-pagination-container">
          <PaginationComponent
            totalRecords={totalRecords || 0}
            pageSize={pageSize}
            currentPage={currentPage}
            onPageChange={handlePageChange}
          />
        </div>
      ) : (
        <></>
      )}
      {isDialogOpen && (
        <AddAdjustmentPopup
          isDialogOpen={isDialogOpen}
          setIsOpen={setIsDialogOpen}
          invoiceId={invoiceId}
          setAlert={setAlert}
        />
      )}
      {alert?.isOpen && (
        <AlertComponent
          message={alert?.message || ""}
          type={
            ["info", "success", "error", "warning"].includes(alert?.type || "")
              ? (alert?.type as "info" | "success" | "error" | "warning")
              : "info"
          }
          open={alert?.isOpen}
          onClose={() => setAlert({})}
        />
      )}
    </div>
  );
};
export default TransactionsList;
